<template>
  <!--
    A live region, so the wait is announced rather than only drawn — a user who
    cannot see the sparkle pulse still hears that an answer is on its way.
  -->
  <span
    role="status"
    aria-live="polite"
    :class="cn('inline-flex items-center gap-1.5 text-brand', props.class)"
  >
    <Sparkles :class="cn('shrink-0 animate-pulse motion-reduce:animate-none', iconClass)" />
    <span class="truncate animate-pulse motion-reduce:animate-none">
      <slot>{{ label }}</slot>
    </span>
  </span>
</template>

<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { Sparkles } from '@lucide/vue'

import { cn } from '@/helpers/cn'

/*
 * "The assistant is deciding" — the sparkle, pulsing, with what it is deciding
 * said in words beside it.
 *
 * One component rather than the same two lines in every surface that waits on a
 * suggestion, because the wording and the animation are the promise: wherever
 * the sparkle pulses, an answer is coming, and where it does not, none is. The
 * three suggestion assistants (account, account type, tags) all surface through
 * this, in a dropdown row, a chip and a list row respectively — hence `class`
 * and `iconClass` rather than size variants.
 */
const props = defineProps<{
  /** What is being decided, e.g. 'Looking for an account…'. Overridable by the slot. */
  label?: string
  /** The sparkle's size, since it sits at three different text sizes. */
  iconClass?: HTMLAttributes['class']
  class?: HTMLAttributes['class']
}>()

const { label = 'Thinking…', iconClass = 'size-3.5' } = props
</script>
